import React from 'react';
import { ArrowLeft, FileText, Loader2 } from 'lucide-react';
import Header from '../../components/Header';
import FormGroupCard from '../../components/field/FormGroupCard';
import MyDraftsList from '../../components/field/MyDraftsList';
import { useAvailableForms } from '../../hooks/useAvailableForms';
import { getApiErrorMessage } from '../../api/client';
import { useBack } from '../../hooks/useBack';

/**
 * The Forms library, for a field worker on ANY vertical.
 *
 * WHY THIS SCREEN EXISTS AT ALL
 * -----------------------------
 * `/my-work/forms` is the same list, and casing hands cannot reach it: that
 * shell is only mounted for trucking drivers and rental techs, and the hook
 * behind it calls `/field/forms`, which resolves a `trucking_drivers` row and
 * 404s without one. A casing tenant has no driver rows, so the answer was a
 * refusal rather than an empty list — and the safety library every tenant gets
 * had no door on this tree at all.
 *
 * This is NOT a second Forms tab. It renders `FormGroupCard`, the same
 * component `/my-work/forms` renders, against the same group shape; only the
 * endpoint and the surrounding field-screen chrome differ. A fork here is how
 * the two lists would start disagreeing about which forms a tenant has.
 */
export default function FieldFormsScreen() {
  const goBack = useBack('/field');
  const { data: groups = [], isLoading, isError, error } = useAvailableForms();

  // Headers earn their space only when there is more than one group to tell
  // apart — the same rule the /my-work list uses.
  const showHeaders = groups.length > 1;

  return (
    <div className="animate-fade-in min-h-full bg-fieldiq-dark pb-[var(--bottom-chrome)]">
      <div className="max-w-[1920px] mx-auto">
        <Header />
        <div className="px-4 py-3 border-b border-fieldiq-border flex items-center gap-3">
          {/* Goes to /field, not back through history. There are several ways
              in — a home tile, the More menu, a job's action bar — and with the
              tab bar gone the home screen is the only navigation there is, so
              every screen in this tree has to reach it in one tap. navigate(-1)
              cannot promise that: a deep link, a redirect or a reload leaves
              nothing useful behind it. */}
          <button onClick={goBack} aria-label="Back" className="text-fieldiq-text">
            <ArrowLeft size={26} />
          </button>
          <h1 className="text-xl font-bold text-white">Forms</h1>
        </div>
      </div>

      <div className="max-w-[1920px] mx-auto px-4 py-4">
        <p className="text-xs text-fieldiq-text">Self-initiated forms and inspections.</p>

        <MyDraftsList />

        {isLoading && (
          <div className="flex justify-center py-12">
            <Loader2 size={28} className="animate-spin text-fieldiq-text" />
          </div>
        )}

        {/* A failed read is not an empty library. "No forms available" on a
            dropped request tells a crew their tenant has nothing, which is a
            claim, and they would stop looking. */}
        {isError && !isLoading && (
          <div className="mt-5 rounded-xl border border-red-500/40 bg-red-500/10 px-4 py-3 text-sm text-red-300">
            {getApiErrorMessage(error, 'Could not load your forms.')}
          </div>
        )}

        {!isLoading && !isError && groups.length === 0 && (
          <div className="text-center py-12">
            <FileText size={40} className="mx-auto text-fieldiq-text/40 mb-3" />
            <p className="text-sm text-fieldiq-text">No forms available</p>
          </div>
        )}

        {!isLoading && !isError && groups.length > 0 && (
          <div className="mt-5 space-y-5">
            {groups.map((group) => (
              <FormGroupCard key={group.group} group={group} showHeader={showHeaders} />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
